import { useEffect, useState, useCallback, Fragment } from 'react'
import { useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { clsx } from 'clsx'

import {
  apolloAuthData,
  apolloEditStatusData,
  setChangesEditStatus,
  getEditStatus,
} from '../services/apollo'
import {
  KnowledgeCheck,
  EditableComponent,
  DialogEditLearningLoungeItemResources,
} from '../components'
import { ROLE_MANAGER, ROLE_FACILITATOR, ROLE_PARTICIPANT } from '../constants'
import { serializeFromQuery } from '../utils'

const LearningLoungeDetail = () => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  const isFacilitator = auth.user?.capabilities?.includes(ROLE_FACILITATOR)
  const isParticipant = auth.user?.capabilities?.includes(ROLE_PARTICIPANT)
  let navigate = useNavigate()
  let location = useLocation()
  const page = location.pathname.split('/').pop()
  let [searchParams, setSearchParams] = useSearchParams()
  const editStatus = useReactiveVar(apolloEditStatusData)
  const isEditOn = isManager && !!searchParams.get('edit')
  const [isDataLoading, setIsDataLoading] = useState(false)
  const [updatedResources, setUpdatedResources] = useState([])
  const [learningLoungeItems, setLearningLoungeItems] = useState([])
  const learningLoungeItem = learningLoungeItems[0] ?? null

  const GET_LEARNING_LOUNGE_ITEMS = gql`
    query LearningLoungeItems {
      learningLoungeItems(where: {postId:"${page}"}) {
        edges {
          node {
            databaseId
            order
            postId
            title
            detail {
              overview
              steps
              note
              resources {
                id
                name
                type
                url
              }
              questions {
                id
                statement
                point
                choices {
                  id
                  correct
                  text
                }
                feedback {
                  correct
                  incorrect
                }
                gradingMethod
              }
            }
          }
        }
      }
    }
  `
  const [getLearningLoungeItems] = useLazyQuery(GET_LEARNING_LOUNGE_ITEMS, {
    fetchPolicy: 'no-cache',
  })

  const loadData = useCallback(async () => {
    setIsDataLoading(true)
    getLearningLoungeItems()
      .then((response) => {
        const _learningLoungeItems =
          response?.data?.learningLoungeItems?.edges?.map(
            (edge) => edge.node
          ) ?? []
        const _learningLoungeItem = _learningLoungeItems[0] ?? null

        const lock =
          isManager || isFacilitator
            ? false
            : isParticipant
            ? !!_learningLoungeItems?.lock
            : true

        if (!_learningLoungeItem || lock) {
          navigate('/learninglounge', { replace: true })
        }

        // Save original data if in edit mode
        if (isEditOn) {
          setChangesEditStatus({
            ...getEditStatus(),
            originalData: {
              ...getEditStatus().originalData,
              questions: _learningLoungeItem?.detail?.questions,
            },
          })
        }

        setLearningLoungeItems(_learningLoungeItems)
      })
      .catch((err) => {
        console.og('load data error :: err', err)
      })
      .finally(() => {
        setIsDataLoading(false)
      })
  }, [
    getLearningLoungeItems,
    isEditOn,
    isManager,
    isFacilitator,
    isParticipant,
    navigate,
  ])

  // Load data every time the page is change
  useEffect(() => {
    if (page !== '') {
      loadData()
    }
  }, [loadData, page])

  // Reload data when publish is successful
  useEffect(() => {
    if (!isEditOn) {
      return
    }

    if (editStatus.published === true) {
      loadData()
    }
  }, [editStatus.published, loadData, isEditOn])

  const handleOpenResourcesEditor = () => {
    // Open up the editor dialog
    setSearchParams({
      ...serializeFromQuery(searchParams),
      editor: 'true',
      resources: 'true',
    })
  }

  const handleResourcesEditorConfirm = (updatedRowData = []) => {
    const newChange = {
      page: 'LEARNING_LOUNGE_ITEM',
      type: 'resources',
      action: 'update',
      databaseId: learningLoungeItem?.databaseId,
      content: updatedRowData,
    }

    setChangesEditStatus({
      ...getEditStatus(),
      changes: [...getEditStatus().changes, newChange],
    })

    // Update the updatedModules state
    setUpdatedResources(updatedRowData)

    // Close the dialog
    handleResourcesEditorCancel()
  }

  const handleResourcesEditorCancel = () => {
    const { editor, resources, ...rest } = serializeFromQuery(searchParams)
    setSearchParams(rest)
  }

  const resources =
    Array.isArray(updatedResources) &&
    updatedResources.length > 0 &&
    editStatus.changes.length > 0
      ? updatedResources
      : learningLoungeItem?.detail?.resources ?? []

  return (
    <>
      {/** Learning Lounge item resources editor */}
      <DialogEditLearningLoungeItemResources
        open={
          searchParams.get('editor') === 'true' &&
          searchParams.get('resources') === 'true'
        }
        rowData={[...resources]}
        title="Learning Lounge Resources Editor"
        yesText="Save"
        noText="Cancel"
        onConfirm={handleResourcesEditorConfirm}
        onCancel={handleResourcesEditorCancel}
      />
      <main className="min-h-[calc(100vh-200px)] overflow-hidden">
        <div className="container mb-[120px] max-w-screen-content pt-14 sm:px-0">
          <div className="relative pb-[10px]">
            <div className="absolute -right-[265px] top-[15px] -z-[1] h-[395px] w-[403px] bg-none bg-contain bg-right-top bg-no-repeat opacity-20 sm:bg-learninglounge-background"></div>
            {isDataLoading ? (
              <LoadingSkeleton />
            ) : learningLoungeItems.length === 0 ? (
              <p className="text-center">No data</p>
            ) : (
              <>
                <h1 className="mb-10 text-center text-4xl font-bold text-skin-accent">
                  {isEditOn ? (
                    <EditableComponent
                      editable={isEditOn}
                      doc={learningLoungeItem?.title ?? '<div></div>'}
                      toolbarOption={{
                        listNumber: false,
                        listBullet: false,
                      }}
                      tag={{
                        page: 'LEARNING_LOUNGE_ITEM',
                        type: 'title',
                        action: 'update',
                        databaseId: learningLoungeItem?.databaseId,
                      }}
                      disableParagraph={true}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: learningLoungeItem?.title ?? '<div></div>',
                      }}
                    />
                  )}
                </h1>
                {/** Overview */}
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    doc={learningLoungeItem?.detail?.overview ?? '<div></div>'}
                    tag={{
                      page: 'LEARNING_LOUNGE_ITEM',
                      type: 'overview',
                      action: 'update',
                      databaseId: learningLoungeItem?.databaseId,
                    }}
                  />
                ) : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        learningLoungeItem?.detail?.overview ?? '<div></div>',
                    }}
                  />
                )}

                {/** Steps to Complete */}
                <h2 className="my-5 font-bold text-skin-accent">
                  Steps to Complete:
                </h2>
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    editableClassName="hover:outline-1 hover:outline-beer focus:outline-1 focus:outline-beer focus:shadow-sm detail-step-list"
                    scrollbarY={false}
                    doc={`<ol>${learningLoungeItem?.detail?.steps
                      ?.map((step) => `<li>${step}</li>`)
                      .join('')}</ol>`}
                    toolbarOption={{
                      listNumber: false,
                      listBullet: false,
                    }}
                    tag={{
                      page: 'LEARNING_LOUNGE_ITEM',
                      type: 'steps',
                      action: 'update',
                      databaseId: learningLoungeItem?.databaseId,
                    }}
                  />
                ) : (
                  <div className="detail-step-list">
                    <ol>
                      {learningLoungeItem.detail?.steps?.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </div>
                )}

                {/** Additional note */}
                {isEditOn ? (
                  <EditableComponent
                    editable={isEditOn}
                    scrollbarY={false}
                    doc={learningLoungeItem?.detail?.note ?? '<p></p>'}
                    placeholder="Enter additional note if any..."
                    tag={{
                      page: 'LEARNING_LOUNGE_ITEM',
                      type: 'note',
                      action: 'update',
                      databaseId: learningLoungeItem?.databaseId,
                    }}
                  />
                ) : (
                  learningLoungeItem.detail?.note && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: learningLoungeItem.detail.note,
                      }}
                    />
                  )
                )}

                {/** CTA */}
                <div className="my-16">
                  <div
                    className={clsx(
                      'flex flex-col items-center justify-center gap-y-4',
                      isEditOn &&
                        'group relative -m-[15px] cursor-pointer p-[15px] outline outline-0 hover:outline-1 hover:outline-beer'
                    )}
                    onClick={isEditOn ? handleOpenResourcesEditor : null}
                  >
                    {isEditOn && (
                      <div
                        className={clsx(
                          'absolute left-0 top-0 z-10 hidden h-full w-full bg-beer/25 group-hover:block'
                        )}
                      ></div>
                    )}
                    {resources?.map((resource, resourceIndex) => {
                      if (resource.type === 'EXTERNAL') {
                        if (isEditOn) {
                          return (
                            <div
                              key={
                                resource.id ??
                                `${resourceIndex}${resource.name}`
                              }
                              className="rounded-full bg-skin-button-accent px-8 pb-3 pt-[0.6rem] font-bold text-white"
                            >
                              {resource.name ?? 'No name'}
                            </div>
                          )
                        } else {
                          return (
                            <a
                              key={
                                resource.id ??
                                `${resourceIndex}${resource.name}`
                              }
                              href={resource.url ?? ''}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded-full bg-skin-button-accent px-8 pb-3 pt-[0.6rem] font-bold text-white transition-colors hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]"
                            >
                              {resource.name ?? 'No name'}
                            </a>
                          )
                        }
                      } else if (resource.type === 'EXTERNAL_ALT') {
                        // External link with alternative style. In this case,
                        // it's a link for member firm.
                        if (isEditOn) {
                          return (
                            <div
                              key={
                                resource.id ??
                                `${resourceIndex}${resource.name}`
                              }
                              className="text-dark-gray"
                            >
                              {resource.name ?? 'No name'}
                            </div>
                          )
                        } else {
                          return (
                            <a
                              key={
                                resource.id ??
                                `${resourceIndex}${resource.name}`
                              }
                              href={resource.url ?? ''}
                              target="_blank"
                              rel="noreferrer"
                              className="text-dark-gray hover:underline"
                            >
                              {resource.name ?? 'No name'}
                            </a>
                          )
                        }
                      } else {
                        return <Fragment key={`${resourceIndex}`}></Fragment>
                      }
                    })}
                  </div>
                </div>

                {/** Knowledge Check questions */}
                <h2 className="font-bold text-skin-accent">Knowledge Check</h2>
                <KnowledgeCheck
                  databaseId={learningLoungeItem?.databaseId}
                  postType="LEARNING_LOUNGE_ITEM"
                  postId={learningLoungeItem.postId}
                  questions={learningLoungeItem.detail?.questions ?? []}
                />
              </>
            )}
          </div>
        </div>
      </main>
    </>
  )
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse space-y-2.5">
      {/** Title */}
      <div className="mb-10 flex w-full items-center justify-center space-x-2">
        <div className="mb-[28px] mt-[28px] h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Overview */}
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="flex w-full max-w-[400px] items-center space-x-2">
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        <div className="h-2.5 w-80 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
      </div>
      <div className="!mt-14 flex w-full items-center space-x-2">
        <div className="mb-6 h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Steps to complete */}
      <div className="flex w-full flex-col">
        <div className="mb-6 ml-6 flex w-full items-center">
          <div className="h-[40px] w-[40px] shrink-0 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="ml-4 h-2.5 w-1/5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
        <div className="mb-6 ml-6 flex w-full items-center">
          <div className="h-[40px] w-[40px] shrink-0 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="ml-4 h-2.5 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
        </div>
        <div className="mb-6 ml-6 flex w-full items-center">
          <div className="h-[40px] w-[40px] shrink-0 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="flex w-full flex-col space-y-2 self-end">
            <div className="ml-4 h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
            <div className="ml-4 h-2.5 w-1/5 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          </div>
        </div>
      </div>
      {/** Additional note */}
      <div className="!mt-10 flex w-full flex-col gap-2">
        <div className="flex w-full items-center space-x-2">
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
          <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        <div className="flex w-full items-center space-x-2">
          <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        <div className="flex w-full items-center space-x-2">
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
          <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        <div className="flex w-full items-center space-x-2">
          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2.5 w-24 rounded-full bg-gray-300 dark:bg-gray-600"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
        <div className="flex w-full max-w-[300px] items-center space-x-2">
          <div className="h-2.5 w-40 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div className="h-2.5 w-full rounded-full bg-gray-300 dark:bg-gray-600"></div>
        </div>
      </div>
      {/** Resource button */}
      <div className="!mt-14 flex w-full flex-col items-center justify-center gap-4">
        <div className="h-[41.5px] w-[195px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
        <div className="h-2.5 w-[195px] rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      {/** Knowledge check */}
      <div className="!mt-16 flex w-full items-center space-x-2">
        <div className="mb-6 h-2.5 w-2/5 rounded-full bg-gray-300 dark:bg-gray-700"></div>
      </div>
      <div className="flex w-full">
        <div className="mb-6 h-[150px] w-full rounded-[9px] bg-gray-300 dark:bg-gray-700"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default LearningLoungeDetail
